import axios from 'axios'
import {
  verifyLogin,
  authFail,
  verifyBigcommerceLogin,
  verifyWoocommerceLogin,
  verifyShopifyLogin,
  checkShopifyExistence,
  inkfrogAuth,
  getCustomerInfoUrl,
  shopifyPlanSelectedUrl,
  getAdminUser,
  signOutUrl,
  dataConnectorsConfigUrl,
  getUserSharedAccountUrl,
  organizationsUrl,
  getUserPermissionsUrl,
  getMyPermissionsUrl,
  getFeatureFlagsUrl,
  completeOnboardingUrl,
  seedOnboardDashboardUrl,
} from 'urls'
import { toast } from 'react-toastify'
import { getLoginInfo, redirectLogin } from 'loginControl'
import hosts from 'hosts.json'
import queryString from 'query-string'
import {
  applyDevPersonaToSessionPermissions,
  captureDevPersonaFromUrl,
  isDevPermissionPersonaEnabled,
} from 'modules/devPermissionPersona'

// Helper to get inkfrog host config with fallback for dynamic staging
function getInkfrogHost(hostname: string) {
  if (hosts.inkfrogHost[hostname]) {
    return hosts.inkfrogHost[hostname]
  }
  // Staging environments use the same inkfrog config as production
  return hosts.inkfrogHost['app.payhelm.com']
}
import { checkRequiredAction } from './actionUtils'
import {
  sendGtmPixel,
  pickPrimaryStoreAccount,
  findLinkedStoreAccount,
  parseNewLinkedAccountCookie,
} from 'tagManager'
import { getAxiosDefaultConfig, localStoragePayhelm } from 'utils'
import _ from 'lodash'
import { getCookie, setCookie } from 'cookie'
import { initializeShopifyAppBridge } from 'modules/shopifySessionToken'

const verifyChannelLoginUrls = {
  Bigcommerce: verifyBigcommerceLogin,
  Woocommerce: verifyWoocommerceLogin,
  Shopify: verifyShopifyLogin,
}

function stripOrgParamFromUrl(params: URLSearchParams): string {
  params.delete('org')
  const nextSearch = params.toString()
  return `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`
}

async function applyOrgDeepLinkFromUrl(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search)
  const targetOrgId = params.get('org')?.trim()
  if (!targetOrgId) return false

  try {
    const listRes = await fetch(organizationsUrl, { credentials: 'include' })
    if (listRes.ok) {
      const body = await listRes.json()
      const organizations = Array.isArray(body?.organizations) ? body.organizations : []
      const alreadyActive = organizations.some(
        (org: { id?: string; is_active?: boolean }) => org.id === targetOrgId && org.is_active
      )
      if (alreadyActive) {
        window.history.replaceState({}, '', stripOrgParamFromUrl(params))
        return false
      }
    }

    const switchRes = await fetch(`${organizationsUrl}/switch`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: targetOrgId }),
    })

    const nextUrl = stripOrgParamFromUrl(params)

    if (switchRes.ok) {
      window.location.replace(nextUrl)
      return true
    }

    const errBody = await switchRes.json().catch(() => ({}))
    toast.error((errBody as { error?: string }).error || 'Could not open organization from link')
    window.history.replaceState({}, '', nextUrl)
  } catch {
    toast.error('Could not open organization from link')
    window.history.replaceState({}, '', stripOrgParamFromUrl(params))
  }

  return false
}

export function changeApplication(currentId, nextId) {
  if (currentId != nextId) {
    return {
      type: 'CHANGE_APPLICATION_ROOT',
      payload: nextId.replace(/\//g, ''),
    }
  } else return {
    type: 'NULL'
  }
}

export function setLinkingStatus(status) {
  return {
    type: 'SET_LINKING_STATUS_ROOT',
    payload: status,
  }
}

export function setChargeStatus(status) {
  return {
    type: 'SET_CHARGE_STATUS_ROOT',
    payload: status,
  }
}

export function setAccounts(accounts) {
  return {
    type: 'SET_ACCOUNTS_ROOT',
    payload: accounts,
  }
}

export function setDomains(domains) {
  return {
    type: 'SET_DOMAINS_ROOT',
    payload: domains,
  }
}

export function loginSuccess(accounts, user, loginFromChannel) {
  return {
    type: 'LOGIN_SUCCESS_ROOT',
    payload: { accounts, user, loginFromChannel },
  }
}

export function loginFailure(message) {
  return {
    type: 'LOGIN_FAILURE_ROOT',
    payload: message,
  }
}

export function updateUserPreferencesRoot(userPreferences) {
  return {
    type: 'UPDATE_USER_PREFERENCES_ROOT',
    payload: userPreferences,
  }
}

export function setAccountFilter(filter) {
  return {
    type: 'SET_ACCOUNT_FILTER_ROOT',
    payload: filter,
  }
}

export function setThirdParty(thirdParty) {
  return {
    type: 'SET_THIRD_PARTY_ROOT',
    payload: thirdParty,
  }
}

export function setInkfrogUser(user) {
  return {
    type: 'SET_INKFROG_USER_ROOT',
    payload: user,
  }
}

export function setInkfrogNeedsUpgrade(flag) {
  return {
    type: 'SET_INKFROG_NEEDS_UPGRADE_ROOT',
    payload: flag,
  }
}

export function toggleAgreement(flag) {
  return {
    type: 'TOGGLE_AGREEMENT_ROOT',
    payload: flag,
  }
}

export function setLinkModalOpenFor(channel) {
  return {
    type: 'SET_LINK_MODAL_OPEN_FOR_ROOT',
    payload: channel,
  }
}

export function setTopUrl(url) {
  return {
    type: 'SET_TOP_URL_ROOT',
    payload: url,
  }
}

export function updateQueryJson() {
  return {
    type: 'UPDATE_QUERY_JSON',
  }
}

export function setAdmin(flag) {
  return {
    type: 'SET_ADMIN',
    payload: flag,
  }
}

export function setPermissions(permissions: string[]) {
  return {
    type: 'SET_PERMISSIONS',
    payload: permissions,
  }
}

export function setFeatureFlags(flags: Record<string, boolean>) {
  return {
    type: 'SET_FEATURE_FLAGS',
    payload: flags || {},
  }
}

/**
 * Hydrate release feature flags from GET /feature-flags into Redux. Cookie-based,
 * so under multi-org it resolves against the active workspace account. Re-run on
 * workspace switch (see reresolveWorkspaceAccess). Fails safe to an empty map so
 * a fetch error leaves features dark (build-time VITE default → OFF).
 */
export const fetchFeatureFlags = () => async (dispatch) => {
  try {
    const res = await axios.get(getFeatureFlagsUrl, getAxiosDefaultConfig())
    dispatch(setFeatureFlags(res.data?.flags || {}))
  } catch {
    dispatch(setFeatureFlags({}))
  }
}

/**
 * Re-resolve the per-session access surface after a multi-org workspace switch.
 * The backend resolves all three (permissions, shared-account access, flags)
 * from the workspace_context_token cookie, so re-fetching after the switch
 * cookie is set rebinds Redux (root.permissions / accessPermissions /
 * featureFlags) to the newly active workspace account. Call this from the org
 * switch success handler once the multi-org slice lands on this branch.
 */
export const reresolveWorkspaceAccess = () => async (dispatch) => {
  await dispatch(fetchFeatureFlags())
  try {
    const res = await axios.get(getMyPermissionsUrl, getAxiosDefaultConfig())
    let nextPermissions: string[] = res.data?.permissions || []
    if (import.meta.env.DEV) {
      captureDevPersonaFromUrl()
      const applied = applyDevPersonaToSessionPermissions(nextPermissions, false)
      nextPermissions = applied.permissions
    }
    dispatch(setPermissions(nextPermissions))
  } catch {
    dispatch(setPermissions([]))
  }
}

/**
 * Re-apply the stored dev permission persona to Redux after `?devPersona=` changes
 * (no reload required). No-op when persona is disabled or not active.
 */
export const syncDevPersonaToRedux = () => (dispatch, getState) => {
  if (!isDevPermissionPersonaEnabled()) return
  captureDevPersonaFromUrl()
  const root = getState().root || {}
  const applied = applyDevPersonaToSessionPermissions(
    root.permissions || [],
    !!root.admin,
  )
  if (!applied.active) return
  dispatch(setPermissions(applied.permissions))
  if (applied.admin !== !!root.admin) {
    dispatch(setAdmin(applied.admin))
    if (applied.admin) {
      dispatch({ type: 'FETCH_USER_ADMIN_FULFILLED', payload: { admin: true } })
    } else {
      dispatch({ type: 'FETCH_USER_ADMIN_REJECTED', payload: null })
    }
  }
}

export function setAccountEmailDict(accounts) {
  return {
    type: 'SET_ACCOUNT_EMAIL_DICT_ROOT',
    payload: accounts,
  }
}

export function updateBaremetricsToken(token) {
  return {
    type: 'UPDATE_BAREMETRICS_TOKEN_ROOT',
    payload: token
  }
}

export function updateIntercomToken(token) {
  return {
    type: 'UPDATE_INTERCOM_TOKEN_ROOT',
    payload: token
  }
}

export function updateSlackNotificationSettings(settings) {
  return {
    type: 'UPDATE_SLACK_NOTIFICATIONS_ROOT',
    payload: settings
  }
}

export function setSessionSignature(signature) {
  return {
    type: 'SET_SESSION_SIGNATURE',
    payload: signature
  }
}

export function setOnboardSkipped(flag) {
  return {
    type: 'SET_ONBOARD_SKIPPED_ROOT',
    payload: flag,
  }
}

// #693: mark onboarding complete + persist the wizard answers, then flip the
// local user flags so routing lets the user into the app (→ /dashboard) without
// waiting for a re-verify. Called at the END of the wizard (finish()).
// `payload` = { accountType?, profile? } (a string is treated as accountType for
// backward compatibility).
export function completeOnboarding(payload = {}) {
  const { accountType = null, profile = null } =
    typeof payload === 'string' ? { accountType: payload } : (payload || {})
  return async (dispatch, getState) => {
    // Optimistically unblock the app; the POST is the durable record.
    dispatch(setOnboardSkipped(true))
    // Persist completion client-side so routing survives a reload independent of
    // /verify timing: `onboarding_completed` short-circuits the derived onboarding
    // branch, and clearing `is_sign_up` (never cleared for no-store accounts) stops
    // it from being treated as a brand-new signup. #693.
    setCookie('onboarding_completed', 'true', 1000 * 60 * 60 * 24 * 365) // 1y — setCookie's default extime=0 expires immediately
    setCookie('is_sign_up', '')
    const patch: any = { requires_onboarding: false }
    if (accountType) patch.account_type = accountType
    if (profile) {
      const current = getState()?.root?.user?.onboarding_profile || {}
      patch.onboarding_profile = { ...current, ...profile }
    }
    dispatch(updateUser(patch))
    try {
      const res = await axios.post(
        completeOnboardingUrl,
        { accountType, profile, complete: true },
        getAxiosDefaultConfig(),
      )
      const data = res?.data || {}
      const followUp: any = {}
      if (data.onboarding_profile) followUp.onboarding_profile = data.onboarding_profile
      if (data.dashboardconfig) followUp.dashboardconfig = data.dashboardconfig
      if (Object.keys(followUp).length) dispatch(updateUser(followUp))
      return data
    } catch (error) {
      console.error('Failed to persist onboarding completion', error)
      return null
    }
  }
}

/**
 * Seed (or re-check) the onboard dashboard preset for an already-completed
 * account. Used by the in-dashboard connect banner for users who finished
 * onboarding before auto-seed existed.
 */
export function seedOnboardDashboard() {
  return async (dispatch) => {
    try {
      const res = await axios.post(seedOnboardDashboardUrl, {}, getAxiosDefaultConfig())
      const data = res?.data || {}
      const followUp: any = {}
      if (data.onboarding_profile) followUp.onboarding_profile = data.onboarding_profile
      if (data.dashboardconfig) followUp.dashboardconfig = data.dashboardconfig
      if (Object.keys(followUp).length) dispatch(updateUser(followUp))
      if (data.seeded) {
        toast.success('Your dashboard is ready')
      } else if (data.reason === 'already_seeded' || data.reason === 'custom_dashboard_exists') {
        toast.info('Your dashboard is already set up')
      } else if (data.reason === 'onboarding_incomplete') {
        toast.error('Finish onboarding before setting up the dashboard')
      } else {
        toast.error('Could not set up the dashboard')
      }
      return data
    } catch (error) {
      console.error('Failed to seed onboard dashboard', error)
      toast.error('Could not set up the dashboard')
      return null
    }
  }
}

// #693: incrementally save a fragment of the onboarding answers as the user
// advances through the wizard. Mirrors the server-side merge into
// user_account.onboarding_profile in Redux so a mid-wizard refresh keeps the
// client blob in sync. `payload` = { accountType?, profile? }.
export function updateOnboardingProfile(payload = {}) {
  const { accountType = null, profile = null, reset = false } = payload || {}
  return async (dispatch, getState) => {
    const patch: any = {}
    if (reset) {
      patch.account_type = accountType || null
      patch.onboarding_profile = profile || {}
    } else {
      if (accountType) patch.account_type = accountType
      if (profile) {
        const current = getState()?.root?.user?.onboarding_profile || {}
        patch.onboarding_profile = { ...current, ...profile }
      }
    }
    if (Object.keys(patch).length) dispatch(updateUser(patch))
    try {
      await axios.post(completeOnboardingUrl, { accountType, profile, reset }, getAxiosDefaultConfig())
    } catch (error) {
      console.error('Failed to persist onboarding profile', error)
    }
  }
}

export function updateDataConnectorSettings({intercomToken, baremetricsToken, slackConfig}) {
  return (dispatch) => {
    intercomToken ? dispatch(updateIntercomToken(intercomToken)) : null
    baremetricsToken ? dispatch(updateBaremetricsToken(baremetricsToken)) : null
    slackConfig ? dispatch(updateSlackNotificationSettings(slackConfig)) : null
  }
}

export const signOut = () => (dispatch, getState) => {
  const { user, accounts } = getState().root
  // #693: clear the onboarding-completion cookie so a shared browser doesn't
  // short-circuit the derived onboarding branch for the next user.
  setCookie('onboarding_completed', '')
  // Affiliate tracking: logout
  if (user) {
    sendGtmPixel({
      email: user.email,
      userId: user.id,
      storeAccount: pickPrimaryStoreAccount(accounts),
    }, 'afftrack_logout')
  }
  return axios.get(signOutUrl, getAxiosDefaultConfig())
}

const checkAdmin = () => {
  return axios.get(getAdminUser, getAxiosDefaultConfig())
}

const fetchUserPermissions = async (unique_user_id: string) => {
  try {
    const res = await axios.get(`${getUserPermissionsUrl}/${unique_user_id}`, getAxiosDefaultConfig())
    return res.data.permissions || []
  } catch (error) {
    console.log('Failed to fetch user permissions', error)
    return []
  }
}

/**
 * General process of login
 * 1, Checked if there is signed_payload in query. If yes, go through the BigCommerce login process first
 * 2, If no signed_payload, check if app_token exists, if no then throw an error
 * 3, Get login account info and customer info (if BC login done then ignore the 1st one)
 * 4, Update the app with responses data
 * @param {String} applicationId the current application id in page
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Shopify checkShopifyExistence call eliminated for returning users (uses cache)
 * - verifyLogin, customer info, and admin check run in one Promise.allSettled batch
 * - Shared-account fetch runs after login resolves so we always use the verified user email
 */
export const appLogin = (applicationId) => {
  return async (dispatch, getState) => {
    let loginFromChannel, email
    try {
      const { app_token, admin_token } = getLoginInfo()
      const queryParams = queryString.parse(location.search)
      let channelLoginRes
      let hasChannelLoginParams = {
        Bigcommerce: !!queryParams.signed_payload,
        Woocommerce: queryParams.channel === 'woocommerce' && !!queryParams.payload,
        Shopify: !!queryParams.hmac && !!queryParams.shop && !!(queryParams.host || queryParams.url),
      }
      let referrerDomain = ''
      try {
        referrerDomain = document.referrer ? (new URL(document.referrer)).hostname : ''
      }
      catch(error) {
        console.error('Failed to get referrer site', document.referrer)
      }

      // Landing from channel iframe
      for (var channel of ['Bigcommerce', 'Woocommerce', 'Shopify']) {
        if (hasChannelLoginParams[channel]) {
          loginFromChannel = channel
          if (channel === 'Shopify') {
            // PERFORMANCE: Only check existence if no app_token (first-time install)
            // Returning users skip this call entirely, saving ~200-500ms
            if (!app_token) {
              const { data: { redirectUrl }} = await axios.get(`${checkShopifyExistence}?shop=${queryParams.shop}`)
              if (redirectUrl) {
                location.href = redirectUrl
                return
              }
            }
          }
          try {
            channelLoginRes = await axios.get(`${verifyChannelLoginUrls[channel]}?${queryString.stringify(queryParams)}`, getAxiosDefaultConfig())
            if (channelLoginRes && channelLoginRes.data && channelLoginRes.data.signature) {
              dispatch(setSessionSignature(channelLoginRes.data.signature))
              if (channel === 'Shopify') {
                // Initialize App Bridge v4 meta tag
                initializeShopifyAppBridge()
              }
              break
            }
          }
          catch(error) {
            console.log('Failed to auth through', channel, error)
          }
        }
      }
      // Woo plugin iframes must not fall through to cookie verifyLogin. Otherwise
      // an active mimic (or leftover app_token) silently takes over when store
      // /verify fails. Do NOT apply this to Shopify/BigCommerce — those embeds
      // often survive a failed channel /verify via app_token + verifyLogin.
      if (loginFromChannel === 'Woocommerce' && !(channelLoginRes?.data?.signature)) {
        throw new Error('Channel authentication failed')
      }
      if (!app_token && !getState().root.sessionSignature) {
        throw('No app token detected')
      }

      const isFromIframe = window.location != window.parent.location
      email = _.get(channelLoginRes, ['user', 'email']) || _.get(channelLoginRes, ['data', 'user', 'email'])
      if (isFromIframe) {
        console.log(`iframe login from ${referrerDomain}`)
        sendGtmPixel({
          email,
          message: `iframe login from ${referrerDomain}`
        }, 'login_iframe')
      }
      if (loginFromChannel === 'Woocommerce' && isFromIframe) {
        const allowedDomain = channelLoginRes?.data?.domain
        if (allowedDomain && referrerDomain && allowedDomain.toLowerCase().replace(/^www\./, '') !== referrerDomain.toLowerCase().replace(/^www\./, '')) {
          sendGtmPixel({
            email,
            message: `Woocommerce login failed from ${referrerDomain}, requiring ${allowedDomain}`
          }, 'login_failure')
          return dispatch(loginFailure('Sorry, this store is not authenticated with PayHelm'))
        }
      }

      // PERFORMANCE: Parallelize verify (or channel session), customer info, and admin
      const [loginResult, customerResult, adminResult] = await Promise.allSettled([
        channelLoginRes ? Promise.resolve(channelLoginRes) : axios.get(verifyLogin, getAxiosDefaultConfig()),
        axios.get(getCustomerInfoUrl, getAxiosDefaultConfig()),
        checkAdmin(),
      ])

      if (loginResult.status === 'rejected') {
        throw new Error('Login verification failed')
      }
      const loginRes = loginResult.value
      const loginPayload = loginRes?.data
      if (!loginPayload) {
        throw new Error('Login verification returned no data')
      }
      email = _.get(loginRes, ['data', 'user', 'email']) || email
      const {
        accounts,
        user,
        signature,
        isOwner,
        owner,
        ownerId,
        domains,
        seatAssigned,
        is_workspace_context: isWorkspaceContext,
        is_organization_workspace: isOrganizationWorkspace,
        requires_onboarding: requiresOnboarding,
        onboarding_completed: onboardingCompleted,
        account_type: accountType,
        onboarding_profile: onboardingProfile,
      } = loginPayload

      const [sharedAccountResult] = await Promise.allSettled([
        axios.get(getUserSharedAccountUrl, {
          ...getAxiosDefaultConfig(),
          params: {
            email: user.email,
          },
        }),
      ])

      // Billing/customer info is not auth. A failure here (e.g. a stale
      // billing_information.shop pointing at an uninstalled Shopify store)
      // must not bounce the user to /login — degrade to empty billing data.
      if (customerResult.status === 'rejected') {
        console.error('Failed to fetch customer info; continuing without billing data:', customerResult.reason)
      }
      // Default customerBalance to 0 in all cases: a rejected request, or a
      // fulfilled one whose response omits it (Shopify billing never returns it),
      // would otherwise make parseFloat(customerBalance / 100) === NaN in Redux.
      const customerData = { customerBalance: 0, ...(customerResult.status === 'fulfilled' ? customerResult.value?.data : null) }
      const {
        subscription,
        addOns,
        addOnPrices,
        prevAddOnNames,
        userCredits,
        creditCards,
        billingChannel,
        managedPricing,
        monthlySales,
        customerBalance,
        autoDepositAmountSetting,
        aiAutoReloadSetting,
        integrationCounts,
        paymentTransferPending,
      } = customerData

      dispatch(setDomains(domains))

      // Handle shared account result
      let access_permission = []
      if (sharedAccountResult.status === 'fulfilled') {
        access_permission = sharedAccountResult.value.data.access_permission
      } else {
        console.log('Shared account check skipped or failed:', sharedAccountResult.reason)
      }

      // Handle admin result
      let admin = false
      if (adminResult.status === 'fulfilled' && adminResult.value?.data?.admin) {
        admin = true
        dispatch(setAdmin(true))
        dispatch({ type: 'FETCH_USER_ADMIN_FULFILLED', payload: { admin: true } })
      } else {
        console.log('Not admin')
        dispatch({ type: 'FETCH_USER_ADMIN_REJECTED', payload: null })
      }

      // Fetch current user's permissions for everyone (RBAC). GET /permissions/me uses cookie, no admin required.
      try {
        const res = await axios.get(getMyPermissionsUrl, getAxiosDefaultConfig())
        let nextPermissions: string[] = res.data.permissions || []
        let nextAdmin = admin
        if (import.meta.env.DEV) {
          captureDevPersonaFromUrl()
          const applied = applyDevPersonaToSessionPermissions(nextPermissions, admin)
          nextPermissions = applied.permissions
          nextAdmin = applied.admin
        }
        if (nextAdmin !== admin) {
          dispatch(setAdmin(nextAdmin))
          if (nextAdmin) {
            dispatch({ type: 'FETCH_USER_ADMIN_FULFILLED', payload: { admin: true } })
          } else {
            dispatch({ type: 'FETCH_USER_ADMIN_REJECTED', payload: null })
          }
        }
        dispatch(setPermissions(nextPermissions))
      } catch {
        dispatch(setPermissions([]))
      }

      // Hydrate release feature flags (master switch layer). Cookie-based, so
      // resolves against the active workspace account under multi-org.
      dispatch(fetchFeatureFlags())

      if (signature) dispatch(setSessionSignature(signature))
      dispatch({ type: 'SET_CREDIT_CARDS_PLAN', payload: creditCards })
      dispatch({ type: 'SET_USER_CREDITS_PLAN', payload: userCredits })
      dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: subscription })
      if (addOns) {
        dispatch({ type: 'SET_ADD_ONS_PLAN', payload: addOns })
      }
      dispatch({ type: 'SET_ADD_ON_PRICES_PLAN', payload: addOnPrices })
      dispatch({ type: 'SET_PREV_ADD_ON_NAMES_PLAN', payload: prevAddOnNames })
      dispatch({ type: 'SET_BILLING_CHANNEL_PLAN', payload: billingChannel })
      dispatch({ type: 'SET_MANAGED_PRICING_PLAN', payload: !!managedPricing })
      dispatch({ type: 'SET_MONTHLY_SALES_PLAN', payload: monthlySales })
      dispatch({ type: 'SET_LAST_LOGIN_DATE_INTERCOM', payload: (new Date().toString()) })
      dispatch({ type: 'SET_CUSTOMER_BALANCE_PLAN', payload: parseFloat(customerBalance / 100) })
      dispatch({ type: 'SET_AUTO_DEPOSIT_AMOUNT_SETTING_PLAN', payload: autoDepositAmountSetting })
      dispatch({ type: 'SET_AI_AUTO_RELOAD_SETTING_PLAN', payload: aiAutoReloadSetting || null })
      dispatch({ type: 'SET_INTEGRATION_COUNTS_PLAN', payload: integrationCounts || null })

      // Shopify App Pricing return: Shopify redirects back to the embedded app
      // with ?plan_handle=... after the merchant picks a plan on the hosted page.
      // Record it, refresh the subscription, and strip the param so a refresh
      // doesn't re-post.
      try {
        const planHandle = new URLSearchParams(window.location.search).get('plan_handle')
        if (managedPricing && planHandle) {
          const { data: updatedSubscription } = await axios.post(
            shopifyPlanSelectedUrl,
            { plan_handle: planHandle },
            getAxiosDefaultConfig()
          )
          dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: updatedSubscription })
          const cleanUrl = new URL(window.location.href)
          cleanUrl.searchParams.delete('plan_handle')
          window.history.replaceState({}, '', cleanUrl.toString())
        }
      } catch (error) {
        console.error('Failed to record Shopify plan selection', error)
      }
      dispatch({
        type: 'SET_PAYMENT_TRANSFER_PENDING_PLAN',
        payload: paymentTransferPending || null,
      })
      dispatch({ type: 'SET_IS_OWNER_ROOT', payload: isOwner })
      dispatch({ type: 'SET_SEAT_ASSIGNED_ROOT', payload: seatAssigned !== false })
      dispatch({ type: 'SET_OWNER_EMAIL_ROOT', payload: owner || null })

      if (requiresOnboarding) {
        // Keep localStorage signup_step / onboarding_path so a mid-wizard
        // refresh resumes the same step instead of restarting at PathChoice.
        dispatch({ type: 'CHANGE_APPLICATION_ROOT', payload: 'onboard' })
      } else if (location.pathname.startsWith('/onboard') && onboardingCompleted) {
        // Only bounce finished users off /onboard. An in-progress (or re-test
        // hatch: account_type=test, onboarding_completed=false) session with
        // leftover stores/billing still has onboarding_completed false — kicking
        // them to /dashboard was dropping resume on refresh.
        dispatch(changeApplication(getState().root.applicationId, 'dashboard'))
        window.history.replaceState({}, '', '/dashboard')
      }

      dispatch(
        loginSuccess(
          accounts,
          {
            ...user,
            access_permission: access_permission,
            owner,
            ownerId,
            is_workspace_context: isWorkspaceContext ?? user?.is_workspace_context,
            is_organization_workspace:
              isOrganizationWorkspace ?? user?.is_organization_workspace,
            requires_onboarding: requiresOnboarding ?? user?.requires_onboarding,
            onboarding_completed: onboardingCompleted ?? user?.onboarding_completed,
            account_type: accountType ?? user?.account_type,
            onboarding_profile: onboardingProfile ?? user?.onboarding_profile,
          },
          loginFromChannel
        )
      )

      const { subscriptionActive, requiredAction } = checkRequiredAction(customerData, admin)
      const planExpired = subscription && subscription.plan_expired
      dispatch({ type: 'SET_SUBSCRIPTION_ACTIVE_PLAN', payload: subscriptionActive })
      dispatch({ type: 'SET_PLAN_EXPIRED_PLAN', payload: planExpired })
      dispatch({ type: 'SET_DONE_ACTION_INTERCOM', payload: requiredAction })

      const planActionRequired = !subscriptionActive || planExpired
      if (planActionRequired) {
        dispatch(changeApplication(applicationId, 'plan'))
      }


      const storeMarketplaceAccounts = accounts.filter(account => account.is_store_marketplace_account)
      const isNewSignup = getCookie('is_sign_up')
      const subscriptionStatus = subscription?.status
      const { merchantId: newLinkedMerchantId, channel: newLinkedChannel } = parseNewLinkedAccountCookie()
      const linkedStoreAccount = findLinkedStoreAccount({
        accounts: storeMarketplaceAccounts,
        channel: loginFromChannel || newLinkedChannel,
        merchantId: newLinkedMerchantId,
      })
      const primaryStoreAccount = pickPrimaryStoreAccount(accounts)
      const storeAccountForTracking = linkedStoreAccount || primaryStoreAccount
      const linkingFulfilled = getCookie('linking_fulfilled') === 'true'
      const isOnboarding = requiresOnboarding || location.pathname.startsWith('/onboard')

      // Affiliate tracking: trial signup (fires for all new signups, not just channel signups)
      // The function itself checks for is_sign_up cookie and trialing status
      sendGtmPixel({
        email: user?.email,
        userId: user?.id,
        subscriptionStatus,
        storeAccount: storeAccountForTracking,
      }, 'afftrack_trial_signup')

      if (storeMarketplaceAccounts.length > 0) {
        sendGtmPixel({
          accounts: storeMarketplaceAccounts,
          storeAccount: linkedStoreAccount || storeMarketplaceAccounts[0],
        }, 'signup_success')
      }

      const planName = subscription?.plans?.[0]?.product || 'Free'
      const integrationCount = integrationCounts?.integrations || 0
      const storeCount = integrationCounts?.stores || 0
      const isOrg = isOrganizationWorkspace || false

      // Affiliate tracking: install or integration_added when returning from channel OAuth
      if (loginFromChannel) {
        const installEventType = isNewSignup ? 'afftrack_install' : 'afftrack_integration_added'
        sendGtmPixel({
          email: user?.email,
          userId: user?.id,
          channel: loginFromChannel,
          integrationCount,
          storeCount,
          gmv: monthlySales || 0,
          plan: planName,
          isOrg,
          storeAccount: linkedStoreAccount,
        }, installEventType)
      }

      // Onboarding: store connected via account linking (email signup → /onboard → link store)
      if (linkingFulfilled && isOnboarding && linkedStoreAccount) {
        sendGtmPixel({
          accounts: [linkedStoreAccount],
          storeAccount: linkedStoreAccount,
        }, 'onboard_success')
      }

      // Clear only when signup is attributed (store connected or channel install).
      // Preserves is_sign_up for email-first signups until they connect a channel.
      if (isNewSignup && (storeMarketplaceAccounts.length > 0 || loginFromChannel)) {
        setCookie('is_sign_up', '')
      }

      // Affiliate tracking: login
      sendGtmPixel({
        email: user?.email,
        userId: user?.id,
        integrationCount,
        storeCount,
        gmv: monthlySales || 0,
        plan: planName,
        isOrg,
        storeAccount: storeAccountForTracking,
      }, 'afftrack_login')

      if (!requiresOnboarding) {
        const redirected = await applyOrgDeepLinkFromUrl()
        if (redirected) return
      }
    }
    catch(error) {
      console.error('appLogin error:', error)
      console.log('appLogin error response:', error?.response)
      console.log('appLogin error data:', error?.response?.data || error?.data)
      console.log('appLogin error status:', error?.response?.status)
      console.log('appLogin redirect reason:', !getCookie('app_token') && getState().root.sessionSignature ? 'session expired' : loginFromChannel ? 'loginFromChannel failure' : error?.data?.error === 'data permission required' ? 'data permission required' : 'general login error')
      sendGtmPixel({
        email,
        message: _.get(error, ['data', 'error']) || _.get(error, ['data']) || error
      }, 'login_failure')
      if (!getCookie('app_token') && getState().root.sessionSignature) {
        return dispatch({ type: 'SET_SESSION_EXPIRED', payload: true })
      }
      else if (loginFromChannel) {
        // Store-less WordPress sites cannot finish wc-auth; send them to the claim
        // screen instead of a dead-end auth failure / cookie fallback.
        let storeLessPayload = false
        try {
          const encoded = typeof queryParams.payload === 'string' ? queryParams.payload : ''
          if (encoded) {
            storeLessPayload = !!JSON.parse(atob(encoded)).store_less
          }
        } catch (e) {
          console.error('Failed to read store_less from channel payload', e)
        }
        if (storeLessPayload && loginFromChannel === 'Woocommerce') {
          if (!window.location.pathname.startsWith('/link-site')) {
            window.location.replace(`/link-site${window.location.search}`)
            return
          }
        }
        dispatch(loginFailure('Authentication failed. Please try again or contact our support.'))
      }
      else if (error.data && error.data.error === 'data permission required') {
        redirectLogin({loginError: true})
      }
      else {
        console.error('App login with error:', error)
        redirectLogin({loginError: true})
      }
      axios.get(authFail, getAxiosDefaultConfig())
    }
  }
}

export const inkfrogLogin = () => {
  return (dispatch) => {
    dispatch(setThirdParty('inkfrog'))
    axios.get(getInkfrogHost(location.hostname).user, {withCredentials: true})
    .then(res => {
      dispatch(setInkfrogUser(res.data))
      axios.post(inkfrogAuth, {user: res.data}, {withCredentials: true})
      .then(res => {
        checkAdmin()
        .then((adminRes) => {
          if (adminRes?.data?.admin) {
            dispatch(setAdmin(true))
          }
          dispatch(setInkfrogNeedsUpgrade(res.data.needs_upgrade))
          dispatch(loginSuccess(res.data.accounts))
        })
        .catch(() => {
          dispatch(setInkfrogNeedsUpgrade(res.data.needs_upgrade))
          dispatch(loginSuccess(res.data.accounts))
        })
      })
      .catch(err => {
        console.log(err)
      })
    })
    .catch(err => {
      console.log(err)
    })
  }
}

export const setSlackConnectorsConfig = (slack_config) => {
  return async (dispatch) => {
    try {
      let dataConnectorsConfigRes = await axios.get(dataConnectorsConfigUrl, getAxiosDefaultConfig())
      const { slackconfig, intercomtoken, baremetricstoken } = dataConnectorsConfigRes.data
      if (slackconfig === null) {
        dispatch(updateDataConnectorsConfig(slack_config, intercomtoken, baremetricstoken))
        dispatch(updateDataConnectorSettings({ baremetricsToken: baremetricstoken, intercomToken: intercomtoken, slackConfig: slack_config}))
      } else {
        dispatch(updateDataConnectorSettings({ baremetricsToken: baremetricstoken, intercomToken: intercomtoken, slackConfig: slackconfig}))
      }
    } catch(err) {
      console.log(err)
    }
  }
}

export const getDataConnectorsConfig = () => {
  return async (dispatch) => {
    try {
      let dataConnectorsConfigRes = await axios.get(dataConnectorsConfigUrl, getAxiosDefaultConfig())
      const { slackconfig, intercomtoken, baremetricstoken } = dataConnectorsConfigRes.data
      dispatch(updateDataConnectorSettings({ baremetricsToken: baremetricstoken, intercomToken: intercomtoken, slackConfig: slackconfig}))
    } catch(err) {
      console.log(err)
    }
  }
}

export const updateDataConnectorsConfig = (slackConfig, intercomToken, baremetricsToken, shipstationConfigs, shippingeasyConfigs) => {
  return async (dispatch) => {
    dispatch({ type: 'SET_DATA_CONNECTORS_STATUS_SETTINGS', payload: 'loading' })
    try {
      let dataConnectorsConfigRes = await axios.put(dataConnectorsConfigUrl, { intercomToken, slackConfig, baremetricsToken, shipstationConfigs, shippingeasyConfigs }, getAxiosDefaultConfig())
      const {slackconfig, intercomtoken, baremetricstoken} = dataConnectorsConfigRes.data
      dispatch({ type: 'SET_DATA_CONNECTORS_ERROR_SETTINGS', payload: null })
      dispatch(updateDataConnectorSettings({ baremetricsToken: baremetricstoken, intercomToken:intercomtoken, slackConfig: slackconfig}))
      dispatch({ type: 'SET_DATA_CONNECTORS_STATUS_SETTINGS', payload: 'load_success' })
    } catch(err) {
      const errMsg = _.get(err, ['response', 'data', 'errors'], 'Errors when saving configurations')
      dispatch({ type: 'SET_DATA_CONNECTORS_STATUS_SETTINGS', payload: 'load_error' })
      dispatch({ type: 'SET_DATA_CONNECTORS_ERROR_SETTINGS', payload: errMsg })
    }
  }
}

export function updateUser(user) {
  return {
    type: 'UPDATE_USER_ROOT',
    payload: user,
  }
}

export function setReportTimeCost(milliseconds) {
  return {
    type: 'SET_REPORT_TIME_COST_ROOT',
    payload: milliseconds,
  }
}
